// Strict API error interface
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: Record<string, string[]>;
}

// Validation error structure
export interface ValidationError {
  field: string;
  message: string;
  code?: string;
}

// Comprehensive error type with strict typing
export type ErrorType = ApiError | { message: string } | string;

// Improved API result with never types for mutual exclusion
export type ApiResult<T> =
  | {
      isSuccess: true;
      result: T;
      error?: never;
      errors?: never;
    }
  | {
      isSuccess: false;
      result?: never;
      error: ApiError;
      errors?: ValidationError[];
    };

// Legacy compatibility type (gradually migrate away from this)
export type LegacyApiResult<T> =
  | { isSuccess: true; result: T; error?: undefined; errors?: undefined }
  | {
      isSuccess: false;
      result?: undefined;
      error?: string | null;
      errors?:
        | Record<string, string[]>
        | Array<{ message: string } | string>
        | unknown;
    };

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  errorMessage?: string;
  validationErrors?: string[];
}

// Generic API response wrapper
export interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
}

// Common pagination types
export interface PaginatedRequest {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageCount: number;
  currentPage: number;
  pageSize: number;
}

// Common search and filter types
export interface SearchRequest {
  searchText?: string;
  filters?: Record<string, unknown>;
}

// API endpoint constants
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    ME: '/api/auth/me',
    STATUS: '/api/auth/status',
  },

  // Projects
  PROJECTS: {
    GET_ALL: '/api/projects',
    GET_BY_ID: (id: number) => `/api/projects/${id}`,
    CREATE: '/api/projects',
    UPDATE: (id: number) => `/api/projects/${id}`,
    DELETE: (id: number) => `/api/projects/${id}`,
  },

  // Sites
  SITES: {
    GET_ALL: '/api/sites',
    GET_BY_ID: (id: number) => `/api/sites/${id}`,
    CREATE: '/api/sites',
    UPDATE: (id: number) => `/api/sites/${id}`,
    DELETE: (id: number) => `/api/sites/${id}`,
  },

  // Photos
  PHOTOS: {
    GET_SITE_PHOTOS: (siteId: number) => `/api/sites/${siteId}/photos`,
    UPLOAD: (siteId: number) => `/api/sites/${siteId}/photos`,
    DELETE: (siteId: number, photoId: number) =>
      `/api/sites/${siteId}/photos/${photoId}`,
    GET_COMPRESSED: (siteId: number) =>
      `/api/sites/${siteId}/photos/compressed`,
  },

  // Materials
  MATERIALS: {
    GET_ALL: '/api/materials',
    GET_SITE_MATERIALS: (siteId: number) => `/api/sites/${siteId}/materials`,
    ADD_TO_SITE: (siteId: number) => `/api/sites/${siteId}/materials`,
  },

  // System Info
  INFO: '/api/info',
} as const;

// Helper function to extract error message from ErrorType or unknown
export function getErrorMessage(
  error?: ErrorType | unknown | null,
  showAlert?: boolean
): string {
  if (!error) return 'Unknown error';

  let message = '';

  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error
  ) {
    const errorMessage = (error as { message: unknown }).message;
    message = typeof errorMessage === 'string' ? errorMessage : 'Unknown error';
  } else {
    message = 'Unknown error';
  }

  // Show toast if requested
  if (showAlert && message) {
    // Dynamic import to avoid circular dependency
    import('@/components/feedback').then(({ showGlobalError }) => {
      showGlobalError(message);
    });
  }

  return message;
}

// HTTP methods
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Request configuration
export interface RequestConfig {
  method: HttpMethod;
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
}
